// 改寫自姊妹專案 pixi_slot（FaFaCat 移植案）已驗證的 src/framework/net/GameServerClient.ts。
// 姊妹專案版本在 connect() 內直接串接 FaFaCat 的登入流程
// （UpdateDeviceInfo → Login → GetGameInfo → GetBalance，並解析 Gateway.RespGetGameInfo 等
// 遊戲/Gateway 專屬 Protobuf 訊息）。這些訊息型別在本專案尚未確認（見 docs/PROTOBUF_SCHEMA.md），
// 因此本版本刻意收斂為更薄的通用層：只負責 WebSocket 連線/重連、RSA→AES 握手、與
// 「送出 cmd+data、收到即回報」的 send()/on('packet', …) 管線。登入/餘額/盤面等流程留給
// 之後的遊戲層（`src/game/net/`）在 Slot026 專屬 schema 確認後接上。
//
// 重連（連線中斷後自動重試，5 次、線性遞增 attempt×2s）為本專案新增邏輯，對應
// `SlotFramework/Network/ServerSetting.asset` 的 ReconnectTimeout=5／ReconnectUnitTime=2 秒；
// 姊妹專案的既有實作沒有這段邏輯。
//
// 已對 Slot026 dev 後端實機驗證（2026-07-17）：無 token 訪客連線 → RSA→AES 握手 → 登入
// （訪客略過 Login）→ GetGameInfo/GetBalance（Code=0）→ spin（Code=0、扣款正確）全流程通過。
// 並修正閒置斷線重連後未重建 session 導致 spin 被拒 code=2 的問題（見 reconnectAndResume）。
import { logger } from '../core/Logger'
import { delay, isAbortError } from '../core/async'
import { EventBus, type EventHandler } from '../events/EventBus'
import {
  decryptPacket,
  encryptPacket,
  importAesKey,
  importHmacKey,
  type SessionKeys,
} from './crypto/EncryptionHelper'
import { generateRsaKeyPair } from './crypto/handshake'
import { logRecv, logSend } from './netLog'
import { decodeResponse, encodePacket, type ResponsePacket } from './PacketCodec'
import { gatewayType } from './proto/gatewayProto'
import { ServerError } from './ServerError'
import { getGameToken, isDemoMode } from './urlParams'

/** Gateway 握手指令名稱——與 FaFaCat 共用同一個 Gateway，確認為框架層通用常數。 */
export const HANDSHAKE_CMD = 'GetAESKey'

export type ConnectionState = 'idle' | 'connecting' | 'open' | 'reconnecting' | 'closed'

export interface GameServerClientEvents {
  readonly stateChange: ConnectionState
  readonly packet: ResponsePacket
  readonly reconnectFailed: undefined
  /**
   * 伺服器主動下推「Kicked」（被踢下線，如他處登入/伺服器維護）；收到後停止自動重連。
   * code 為封包的 ResponseInfo.Code，供上層照 Unity BaseGameService.OnKicked 顯示 Error_Code_{code}。
   */
  readonly kicked: { readonly reason: string; readonly code: number }
  /**
   * 收到的封包解密／解析失敗（資料毀損或金鑰不同步，已無法信任這條連線）。
   * 對應 Unity GameServerHandler 解析 ResponseInfo 例外後的 ShowError(Error_Disconnect) + DisConnect。
   */
  readonly packetError: { readonly error: unknown }
  /** 其他伺服器主動下推指令（無對應送出請求），如 ClientClick 等；供上層視需要處理。 */
  readonly serverCommand: { readonly cmd: string; readonly packet: ResponsePacket }
}

/** 伺服器主動下推的指令名稱（無對應送出請求）。對應 Unity Gateway 的 "Kicked"/"ClientClick" 路由。 */
const SERVER_KICKED_CMD = 'Kicked'

export interface GameServerClientOptions {
  /** 最大重連次數（含首次連線失敗後的重試）。對應 ServerSetting.asset 的 ReconnectTimeout。預設 5。 */
  readonly maxReconnectAttempts?: number
  /** 線性遞增等待時間的基數（毫秒）；實際等待 = attempt × this。對應 ReconnectUnitTime。預設 2000。 */
  readonly reconnectBaseDelayMs?: number
  /** 單一請求逾時（毫秒）。預設 15000。 */
  readonly requestTimeoutMs?: number
}

/** 連線後初始化（登入／取遊戲資訊／餘額）所需參數。connect() 帶入時才會跑登入流程。 */
export interface SessionOptions {
  /** 遊戲名稱（送 GetGameInfo）。⚠️ Slot026 的實際 GameName 待後端確認。 */
  readonly gameName: string
  /** 幣別（送 Login／GetGameInfo／GetBalance 的 hint；後端回應為權威值）。 */
  readonly currency: string
}

interface PendingRequest {
  readonly resolve: (response: ResponsePacket) => void
  readonly reject: (error: unknown) => void
  readonly timer: ReturnType<typeof setTimeout>
}

const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5
const DEFAULT_RECONNECT_BASE_DELAY_MS = 2_000
const DEFAULT_REQUEST_TIMEOUT_MS = 15_000

/**
 * 通用 WebSocket 客戶端——處理連線/重連、RSA→AES 握手、與加密封包送收。
 * 不含任何遊戲專屬的登入/餘額/盤面流程，維持 dormant（尚未接上真實後端）。
 */
export class GameServerClient {
  private readonly events = new EventBus<GameServerClientEvents>()
  private readonly pending = new Map<string, PendingRequest>()
  private readonly maxReconnectAttempts: number
  private readonly reconnectBaseDelayMs: number
  private readonly requestTimeoutMs: number

  private ws: WebSocket | null = null
  private keys: SessionKeys | null = null
  private url: string | null = null
  private state: ConnectionState = 'idle'
  private closedIntentionally = false
  private reconnectAbort: AbortController | null = null
  // connect() 帶入的登入參數；重連後用來重建 session（見 reconnectAndResume）。
  private sessionOptions: SessionOptions | null = null

  // 登入流程取得的伺服器權威值（connect() 帶 SessionOptions 時才會填入）。
  private serverBalance = 0n
  private serverCurrency = ''
  private gameBetRange: readonly bigint[] = []
  private gameMaxWin = 0
  private gameRtp: readonly string[] = []
  private gameShowRtp = false

  constructor(options: GameServerClientOptions = {}) {
    this.maxReconnectAttempts = options.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS
    this.reconnectBaseDelayMs = options.reconnectBaseDelayMs ?? DEFAULT_RECONNECT_BASE_DELAY_MS
    this.requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS
  }

  get connectionState(): ConnectionState {
    return this.state
  }

  /** 伺服器權威餘額（整數單位，×10000；未登入時為 0n）。 */
  get balance(): bigint {
    return this.serverBalance
  }

  /** 伺服器權威幣別（未登入時為空字串）。 */
  get currency(): string {
    return this.serverCurrency
  }

  /** GetGameInfo 回傳的合法押注區間（整數單位；未取得時為空陣列）。 */
  get betRange(): readonly bigint[] {
    return this.gameBetRange
  }

  /** GetGameInfo 回傳的單局最高贏分倍數（未取得時為 0）。 */
  get maxWin(): number {
    return this.gameMaxWin
  }

  /**
   * ClientOptions.RTP：index 0 為主遊戲 RTP、其後對應各 BuyType（未取得時為空陣列）。
   * 對照 Unity `ClientOptions.cs` 與 `InfoCommonView.SetLocalize`。
   */
  get rtp(): readonly string[] {
    return this.gameRtp
  }

  /** ClientOptions.ShowRTP：後端是否允許在資訊頁顯示 RTP。 */
  get showRtp(): boolean {
    return this.gameShowRtp
  }

  /** 訂閱連線狀態變化 / 收到的封包 / 重連放棄事件，回傳取消訂閱函式。 */
  on<K extends keyof GameServerClientEvents>(
    type: K,
    handler: EventHandler<GameServerClientEvents[K]>,
  ): () => void {
    return this.events.on(type, handler)
  }

  /**
   * 開啟連線並完成 RSA→AES 握手；若帶入 options，握手成功後接著跑登入流程
   * （UpdateDeviceInfo → Login → GetGameInfo → GetBalance）。連線中斷時內部依重連規則自動重試
   * （重連只恢復連線與握手，不重跑登入流程）。
   */
  async connect(url: string, options?: SessionOptions): Promise<void> {
    this.url = url
    this.sessionOptions = options ?? null
    this.closedIntentionally = false
    await this.connectWithRetry(0, false)
    if (this.sessionOptions !== null) {
      await this.initSession(this.sessionOptions)
    }
  }

  // 送出指令並等待對應 RequestId 的回應（握手前為明文，握手後走 AES-CBC + HMAC）。
  async send(cmd: string, data: Uint8Array): Promise<ResponsePacket> {
    const ws = this.ws
    if (ws === null || ws.readyState !== WebSocket.OPEN) {
      throw new Error('尚未連線')
    }

    const requestId = crypto.randomUUID()
    logSend(cmd, requestId, data)
    const packet = encodePacket({ cmd, requestId, data })
    const framed = this.keys !== null ? await encryptPacket(packet, this.keys) : packet

    return new Promise<ResponsePacket>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId)
        reject(new Error(`請求逾時：${cmd}`))
      }, this.requestTimeoutMs)
      this.pending.set(requestId, { resolve, reject, timer })
      ws.send(framed)
    })
  }

  /** 主動關閉連線；不會觸發自動重連，並中止任何等待中的重連延遲。 */
  close(): void {
    this.closedIntentionally = true
    this.reconnectAbort?.abort()
    this.reconnectAbort = null
    this.ws?.close()
    this.ws = null
    this.keys = null
    this.failAll(new Error('連線已關閉'))
    this.setState('closed')
  }

  private async connectWithRetry(attempt: number, isReconnect: boolean): Promise<void> {
    this.setState(attempt === 0 && !isReconnect ? 'connecting' : 'reconnecting')
    try {
      await this.open(this.requireUrl())
      await this.handshake()
      this.setState('open')
    } catch (error) {
      if (attempt + 1 >= this.maxReconnectAttempts) {
        this.setState('closed')
        this.events.emit('reconnectFailed', undefined)
        throw error
      }
      const waitMs = (attempt + 1) * this.reconnectBaseDelayMs
      logger.warn(
        `連線失敗，${waitMs}ms 後重試（第 ${attempt + 1}/${this.maxReconnectAttempts} 次）`,
        error,
      )
      this.reconnectAbort = new AbortController()
      await delay(waitMs, this.reconnectAbort.signal)
      await this.connectWithRetry(attempt + 1, isReconnect)
    }
  }

  private requireUrl(): string {
    if (this.url === null) throw new Error('尚未指定連線位址')
    return this.url
  }

  private open(url: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(url)
      this.ws = ws
      ws.binaryType = 'arraybuffer'
      // hasOpened／settled 分開追蹤：real WebSocket 對「連線失敗」通常會先觸發 onerror
      // 再觸發 onclose（同一次失敗兩個事件都會來），若只用單一旗標會讓 onclose 誤判成
      // 「已成功開啟後才中斷」，進而觸發多餘的 handleUnexpectedClose() 平行重連。
      let hasOpened = false
      let settled = false
      ws.onopen = () => {
        hasOpened = true
        if (settled) return
        settled = true
        resolve()
      }
      ws.onerror = () => {
        if (settled) return
        settled = true
        reject(new Error('WebSocket 連線失敗'))
      }
      ws.onclose = () => {
        if (!hasOpened) return // 從未成功開啟；onerror 已負責 reject，這裡不重複處理
        this.handleUnexpectedClose()
      }
      ws.onmessage = (event) => {
        void this.onBinary(event.data as ArrayBuffer)
      }
    })
  }

  // GetAESKey：送 RSA 公鑰(SPKI DER)，伺服器以 PKCS#1 v1.5 加密 AES 金鑰回傳。握手前為明文。
  private async handshake(): Promise<void> {
    this.keys = null
    const keyPair = generateRsaKeyPair()
    const response = await this.send(HANDSHAKE_CMD, keyPair.publicKeyDer)
    if (response.code !== 0) throw new Error(`GetAESKey 失敗 code=${response.code}`)
    const aesRaw = keyPair.decryptAesKey(response.data)
    this.keys = {
      aesKey: await importAesKey(aesRaw),
      hmacKey: await importHmacKey(aesRaw),
    }
  }

  // ── 登入流程（connect 帶 SessionOptions 時執行）──────────────────────────────
  // 對應姊妹專案 pixi_slot 的 initSession 序列，與後端共用同一個 Gateway。
  // 已對 dev 後端實機驗證：GameName='Slot026' → 後端回 GameCode='RedRidingHood'，訪客（無 token）可玩。
  private async initSession(options: SessionOptions): Promise<void> {
    this.serverCurrency = options.currency

    await this.send('UpdateDeviceInfo', this.buildDeviceInfo())

    const token = getGameToken()
    if (token !== null && token.length > 0) {
      const login = await this.send('Login', this.encodeGateway('LoginCmd', { Token: token }))
      // 對照 Unity BaseGameService.OnLogin（cs:379-403）：Login 回應 Code!=0 顯示 Error_Code_{code}。
      // transport 層不碰 UI，帶著 code 往上拋（同 RemoteSpinService 的 spin 失敗），由 main.ts 顯示對話框。
      if (login.code !== 0) throw new ServerError(login.code, login.message)
      if (login.currency.length > 0) this.serverCurrency = login.currency
    } else {
      // 無 token：以訪客身分連線（略過 Login）。dev 後端會發訪客錢包，已對 Slot026 實機驗證
      // 可正常遊玩（見 docs/BACKEND_CONNECTION.md）。?demo=true／?r 與純無 token 對後端
      // 行為相同（LoginCmd 只有 Token 欄位、無 demo 旗標），此處僅作 log 區分。
      logger.info(isDemoMode() ? 'Demo 模式：以訪客身分連線（略過登入）' : '訪客模式：無 token，以訪客身分連線（略過登入）')
    }

    const gameInfo = await this.send(
      'GetGameInfo',
      this.encodeGateway('GetGameInfoCmd', { GameName: options.gameName, Currency: options.currency }),
    )
    if (gameInfo.code === 0) this.applyGameInfo(gameInfo.data)

    const balance = await this.send('GetBalance', this.encodeGateway('GetBalanceCmd', { Currency: options.currency }))
    if (balance.code === 0) {
      this.serverBalance = balance.balance
      if (balance.currency.length > 0) this.serverCurrency = balance.currency
    }
  }

  private encodeGateway(typeName: string, payload: Record<string, unknown>): Uint8Array {
    const type = gatewayType(typeName)
    return type.encode(type.create(payload)).finish()
  }

  private buildDeviceInfo(): Uint8Array {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
    const resolution =
      typeof window !== 'undefined' && window.screen !== undefined
        ? `${window.screen.width}x${window.screen.height}`
        : ''
    return this.encodeGateway('DeviceInfo', {
      Manufacturer: '',
      Brand: '',
      Resolution: resolution,
      Network: '',
      SysType: 'Web',
      SysVer: '',
      Browser: ua,
      BrowserVer: '',
      DeviceId: '',
      Ver: '1.0.0',
    })
  }

  private applyGameInfo(data: Uint8Array): void {
    const type = gatewayType('RespGetGameInfo')
    const info = type.toObject(type.decode(data), { longs: String, defaults: true }) as {
      BetRange?: string[]
      MaxWin?: number
      ClientOptions?: string
    }
    this.gameBetRange = (info.BetRange ?? []).map((value) => BigInt(value))
    this.gameMaxWin = info.MaxWin ?? 0
    this.applyClientOptions(info.ClientOptions ?? '')
  }

  /**
   * `ClientOptions` 是後端塞在 GetGameInfo 內的 JSON 字串。
   * 欄位以 Unity `SlotFramework/Script/Data/ClientOptions.cs` 為準：
   * `{ DefaultBetIndex: int, RTP: string[], Skin: string, ShowRTP: bool }`。
   * 這是外部資料，格式不合就退回預設值（資訊頁會改顯示既有的示意 RTP），不讓它中斷登入流程。
   */
  private applyClientOptions(json: string): void {
    this.gameRtp = []
    this.gameShowRtp = false
    if (json.length === 0) return
    try {
      const parsed: unknown = JSON.parse(json)
      if (typeof parsed !== 'object' || parsed === null) return
      const options = parsed as { RTP?: unknown; ShowRTP?: unknown }
      if (Array.isArray(options.RTP)) {
        this.gameRtp = options.RTP.filter((value): value is string => typeof value === 'string')
      }
      this.gameShowRtp = options.ShowRTP === true
    } catch (error) {
      logger.warn('ClientOptions JSON 解析失敗，改用預設值', error)
    }
  }

  // 連線建立「後」才發生的中斷才會走到這裡（見 open() 內 settled 判斷），故一律視為需要重連。
  private handleUnexpectedClose(): void {
    this.ws = null
    this.keys = null
    this.failAll(new Error('連線已關閉'))
    if (this.closedIntentionally) return
    void this.reconnectAndResume().catch((error) => {
      if (isAbortError(error)) return
      logger.error('重連已達上限，放棄連線', error)
    })
  }

  // 重連恢復連線＋握手；若原本帶登入流程，需一併重建 session。
  // 訪客（無 token）斷線後後端不會保留 session，若不重跑登入四連，重連後的 spin
  // 會被後端以 code=2 拒絕（已對 dev 後端實機驗證）。不帶 SessionOptions 的連線（如純握手
  // 測試）維持原本「只恢復連線」行為。
  private async reconnectAndResume(): Promise<void> {
    await this.connectWithRetry(0, true)
    if (this.sessionOptions !== null) {
      await this.initSession(this.sessionOptions)
    }
  }

  private async onBinary(buffer: ArrayBuffer): Promise<void> {
    try {
      let payload: Uint8Array = new Uint8Array(buffer)
      if (this.keys !== null) {
        payload = await decryptPacket(payload, this.keys)
      }
      const response = decodeResponse(payload)
      logRecv(response)
      this.events.emit('packet', response)
      const waiter = this.pending.get(response.requestId)
      if (waiter !== undefined) {
        clearTimeout(waiter.timer)
        this.pending.delete(response.requestId)
        waiter.resolve(response)
      } else {
        // 無對應送出請求 = 伺服器主動下推指令（Kicked/ClientClick 等）。
        this.handleServerCommand(response)
      }
    } catch (error) {
      // 對照 Unity GameServerHandler：解析失敗即中止連線並向玩家提示 Error_Disconnect
      // （金鑰不同步／資料毀損後續封包都不會正確，靜默吞掉只會讓畫面卡住）。
      logger.error('封包解析失敗，中止連線', error)
      this.events.emit('packetError', { error })
      this.close()
    }
  }

  /** 分派伺服器主動下推指令：Kicked → 停止重連並通知上層；其餘 → 廣播 serverCommand 供上層處理。 */
  private handleServerCommand(response: ResponsePacket): void {
    if (response.cmd === SERVER_KICKED_CMD) {
      logger.warn('收到伺服器 Kicked：被踢下線，停止自動重連')
      this.closedIntentionally = true // 被踢下線不自動重連
      this.events.emit('kicked', { reason: response.cmd, code: response.code })
      this.ws?.close()
      return
    }
    this.events.emit('serverCommand', { cmd: response.cmd, packet: response })
  }

  private failAll(error: unknown): void {
    for (const waiter of this.pending.values()) {
      clearTimeout(waiter.timer)
      waiter.reject(error)
    }
    this.pending.clear()
  }

  private setState(next: ConnectionState): void {
    this.state = next
    this.events.emit('stateChange', next)
  }
}
